import { DndContext, DragOverlay } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { type ComponentProps, type ForwardRefExoticComponent, type RefAttributes, useState } from 'react'

import { EditableComponent } from '@/components/EditableComponent.tsx'
import { SortableTask, Task } from '@/components/form/Task.tsx'
import { Label } from '@/components/ui/label.tsx'
import { useDragAndDropSettings } from '@/components/useDragAndDropSettings.ts'
import { DEFAULT_TASK } from '@/constants/DEFAULT_VALUES.ts'
import { TEMPORARY_ID_PREFIX } from '@/constants/PREFIX.ts'
import { useMoveTask } from '@/requests/mutations/useMoveTask.ts'
type EditableVersionProps = {
  storyId: string
  value?: Task[]
  placeholder: string
}

export const EditableTasks = ({ storyId, value = [], placeholder }: EditableVersionProps) => {
  const [activeId, setActiveId] = useState<string | null>(null)
  const { sensors } = useDragAndDropSettings()
  const move = useMoveTask()

  const handleDragStart = (activeId: string) => {
    setActiveId(activeId)
  }

  const handleDragEnd = (overId: string | undefined) => {
    if (!overId || activeId === overId) {
      setActiveId(null)
      return
    }
    if (!activeId) return

    move(storyId, activeId, overId)

    setActiveId(null)
  }

  const draggedTask = value.find((task) => task.id === activeId)

  return (
    <>
      <Label variant="title">
        Tasks ({value.filter((task) => task.finished).length}/{value.length})
      </Label>
      <div className="flex flex-col gap-1 mt-2 mb-1">
        <DndContext
          sensors={sensors}
          onDragStart={({ active }) => handleDragStart(active.id as string)}
          onDragEnd={({ over }) => handleDragEnd(over?.id as string | undefined)}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext items={value.map((task) => task.id)} strategy={verticalListSortingStrategy}>
            {value.map((task) => (
              <SortableTask key={task.id} modes={['view', 'edit']} task={task} placeholder={placeholder} />
            ))}
          </SortableContext>
          {draggedTask && (
            <DragOverlay dropAnimation={null}>
              <div className="opacity-45">
                <Task task={draggedTask} modes={['view']} />
              </div>
            </DragOverlay>
          )}
        </DndContext>
      </div>
      <Task
        modes={['edit']}
        task={DEFAULT_TASK(storyId, value[value.length - 1]?.id ?? null, `${TEMPORARY_ID_PREFIX}${storyId}`)}
        placeholder={placeholder}
      />
    </>
  )
}

export const TypedEditableTasks = EditableComponent as unknown as ForwardRefExoticComponent<
  EditableProps<Task[], undefined, ComponentProps<typeof EditableTasks>> & RefAttributes<EditableCommands>
>
